import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Manages image loading state with delayed spinner display
 * Keeps previous image visible while new image loads
 */
export const useImageLoading = (currentFileId: string | number | undefined, currentImageUrl: string | undefined) => {
  // Always start with loading false - loading state is only used during navigation
  // First load doesn't use the preload mechanism, so no loading state needed
  const [imageLoading, setImageLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  // Start with undefined so the preload kicks in for the first image
  const [displayedImageUrl, setDisplayedImageUrl] = useState<string | undefined>(undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isFirstLoad = useRef(true);

  const handleLoadStart = useCallback(() => {
    setImageLoading(true);
    setShowSpinner(false);

    // Show spinner after 300ms delay
    timerRef.current = setTimeout(() => {
      setShowSpinner(true);
    }, 300);
  }, []);

  const handleLoadComplete = useCallback(() => {
    setImageLoading(false);
    setShowSpinner(false);
    setDisplayedImageUrl(currentImageUrl);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, [currentImageUrl]);

  // Reset loading state when file changes
  // Note: This useEffect is necessary to sync with external state changes (file navigation)
  useEffect(() => {
    // If no image URL (non-image file or undefined), immediately set loading to false
    if (!currentImageUrl) {
      setImageLoading(false);
      setShowSpinner(false);
      setDisplayedImageUrl(undefined);
      isFirstLoad.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      return;
    }

    // On first load OR when displayedImageUrl is undefined (e.g., after viewing non-image),
    // immediately show the image without preload mechanism
    if (isFirstLoad.current || !displayedImageUrl) {
      setDisplayedImageUrl(currentImageUrl);
      setImageLoading(false);
      setShowSpinner(false);
      isFirstLoad.current = false;
      return;
    }

    // For subsequent loads (navigation), use preload mechanism
    if (currentImageUrl !== displayedImageUrl) {
      handleLoadStart();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentFileId, currentImageUrl, displayedImageUrl, handleLoadStart]);

  return {
    imageLoading,
    showSpinner,
    displayedImageUrl,
    currentImageUrl,
    handleLoadComplete
  };
};
